import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  Card,
  EmptyState,
  ErrorState,
  Input,
  Loading,
  PageHeader,
  Select,
  StatCard,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { ChannelMeter, LicenseStatusBadge } from '@/components/License';
import { formatDay, formatMoney } from '@/lib/format';
import { BILLING_MODE_LABELS, daysLeftText, type LicenseStatus, type LicenseView } from '@/lib/license';

interface Row extends LicenseView {
  id: string;
  name: string;
  client_status: string;
  outstanding: number;
}

interface Overview {
  clients: Row[];
  mrr: number;
  by_status: Partial<Record<LicenseStatus, number>>;
}

const FILTERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All clients' },
  { value: 'attention', label: 'Needs attention' },
  { value: 'licensed', label: 'On a licence' },
  { value: 'not_enforced', label: 'Not on a licence' },
];

/**
 * Every client's licence on one screen: who is paid up, who renews this
 * week, who is running on grace and who has stopped. The page somebody opens
 * on the first of the month.
 */
export default function Licensing() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['licensing'],
    queryFn: () => api.get<Overview>('/admin/licensing'),
    refetchInterval: 30_000,
  });

  const rows = useMemo(() => {
    const all = data?.clients ?? [];
    return all.filter((row) => {
      if (search && !row.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (filter === 'attention') return ['expiring', 'grace', 'expired'].includes(row.status) || row.outstanding > 0;
      if (filter === 'licensed') return row.enforced;
      if (filter === 'not_enforced') return !row.enforced;
      return true;
    });
  }, [data, filter, search]);

  const count = (status: LicenseStatus) => data?.by_status[status] ?? 0;

  return (
    <div>
      <PageHeader
        title="Licensing"
        description="Channels sold per client, what is in use right now, and when each licence renews."
      />
      {isPending ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={() => refetch()} />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard label="Monthly recurring revenue" value={formatMoney(data.mrr)} hint="Licences not expired" />
            <StatCard label="Active" value={count('active') + count('no_expiry')} tone="good" />
            <StatCard label="Renewing within 7 days" value={count('expiring')} tone={count('expiring') ? 'warn' : 'default'} />
            <StatCard label="In grace" value={count('grace')} tone={count('grace') ? 'bad' : 'default'} />
            <StatCard label="Expired" value={count('expired')} tone={count('expired') ? 'bad' : 'default'} hint="Calls refused" />
          </div>

          <Card
            title="Clients"
            padded={false}
            actions={
              <>
                <Input placeholder="Search clients" value={search} onChange={(e) => setSearch(e.target.value)} className="w-48" />
                <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-44">
                  {FILTERS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </>
            }
          >
            {rows.length === 0 ? (
              <EmptyState title="No clients match" />
            ) : (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>Client</TH>
                      <TH>Licence</TH>
                      <TH className="min-w-[180px]">AI Bot</TH>
                      <TH className="min-w-[180px]">Soundboard</TH>
                      <TH>Renews</TH>
                      <TH>Billing</TH>
                      <TH numeric>Monthly</TH>
                      <TH numeric>Outstanding</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {rows.map((row) => (
                      <TR key={row.id} onClick={() => navigate(`/clients/${row.id}?tab=license`)}>
                        <TD className="font-medium text-ink-900 dark:text-ink-50">{row.name}</TD>
                        <TD>
                          <LicenseStatusBadge status={row.status} />
                        </TD>
                        {row.engines.map((engine) => (
                          <TD key={engine.kind}>
                            {row.enforced ? <ChannelMeter engine={{ ...engine, label: '' }} compact /> : <span className="text-xs text-ink-400">—</span>}
                          </TD>
                        ))}
                        <TD className="whitespace-nowrap">
                          {row.expires_at ? (
                            <>
                              {formatDay(row.expires_at)}
                              <span className="block text-2xs text-ink-500">{daysLeftText(row)}</span>
                            </>
                          ) : (
                            <span className="text-ink-400">—</span>
                          )}
                        </TD>
                        <TD className="text-xs">{BILLING_MODE_LABELS[row.billing_mode]}</TD>
                        <TD numeric>{row.enforced ? formatMoney(row.monthly_total, row.currency) : '—'}</TD>
                        <TD numeric className={row.outstanding > 0 ? 'font-medium text-amber-600 dark:text-amber-400' : ''}>
                          {formatMoney(row.outstanding, row.currency)}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
